/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DETAIL_HERO_LAYOUT
 * Date:         2026-02-10
 * Modified By:  Claude
 *
 * Description:
 * Redesigned to hero banner + sidebar layout per broadcast-item-detail.html mockup.
 * Hero shows title + meta badges, body has specs table + sticky company sidebar.
 *
 * File Size Check:
 * - Current: 215 lines
 * - Adding: ~30 lines for hero section
 * - Projected: 245 lines
 * - Decision: Proceed (within page limit)
 *
 * Related Files:
 * - client/src/pages/ItemDetailPage/ItemDetailPage.scss
 * - client/src/pages/ItemDetailPage/components/ItemInformation.tsx
 * - client/src/pages/ItemDetailPage/components/CompanyInformation.tsx
 */

/**
 * Broadcast Item Detail Page – hero + sidebar layout per mockup.
 * Fetches a single broadcast, selects item by itemNo, reuses ItemInformation,
 * CompanyInformation, and RecentBroadcasts.
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Spinner, Alert, Button } from "react-bootstrap";
import { getBroadcastById } from "../../../../services/broadcastService";
import { ItemDetail, SellerInfo } from "../../../../pages/ItemDetailPage/interfaces/itemDetail";
import { BroadcastUser } from "../broadcast-details/interfaces/broadcastDetail";
import { notify } from "../../../../utils/notifications";
import ItemInformation from "../../../../pages/ItemDetailPage/components/ItemInformation";
import CompanyInformation from "../../../../pages/ItemDetailPage/components/CompanyInformation";
import RecentBroadcasts from "../../../../pages/ItemDetailPage/components/RecentBroadcasts";
import "../../../../pages/ItemDetailPage/ItemDetailPage.scss";

const BroadcastItemDetailPage: React.FC = () => {
  const { broadcastId, itemNo } = useParams<{ broadcastId: string; itemNo: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [broadcastType, setBroadcastType] = useState<"WTS" | "WTB">("WTS");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // [2026-02-10] Format price with currency for hero banner
  const sym = (c?: string) => (c === "EUR" ? "€" : c === "GBP" ? "£" : "$");
  const formatPrice = (price?: number, currency?: string) => {
    if (price === undefined || price === null) return null;
    const formatted = Math.round(price).toLocaleString("en-US");
    return `${sym(currency)}${formatted}`;
  };

  // [2026-02-10] Get condition badge class
  const getConditionClass = (condition?: string) => {
    if (!condition) return "";
    const normalized = condition.toLowerCase().trim();
    if (normalized.includes("new")) return "badge-condition--new";
    if (normalized.includes("used")) return "badge-condition--used";
    if (normalized.includes("refurbished")) return "badge-condition--refurbished";
    return "";
  };

  useEffect(() => {
    const fetchBroadcast = async () => {
      if (!broadcastId || !itemNo) {
        setError("Broadcast ID and item number are required");
        setLoading(false);
        return;
      }

      const parsedItemNo = parseInt(itemNo, 10);
      if (Number.isNaN(parsedItemNo)) {
        setError("Invalid item number");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getBroadcastById(broadcastId);
        type BroadcastPayload = {
          _id: string;
          userId: BroadcastUser;
          broadcast: Array<{
            itemNo: number;
            manufacturer: string;
            partNumber?: string | null;
            model?: string | null;
            title?: string | null;
            quantity: number;
            condition?: string | null;
            price?: number | null;
            currency?: string | null;
            categories?: string[];
            description?: string | null;
            country?: string | null;
          }>;
        };
        const broadcastData = (response.data as { data?: BroadcastPayload & { type?: "WTS" | "WTB" } }).data;

        if (!broadcastData?.broadcast?.length) {
          setError("Broadcast not found");
          setLoading(false);
          return;
        }

        const bcItem = broadcastData.broadcast.find((i) => i.itemNo === parsedItemNo);
        if (!bcItem) {
          setError("Item not found");
          setLoading(false);
          return;
        }

        // [2026-02-10] Capture broadcast type for hero badge
        setBroadcastType(broadcastData.type || "WTS");

        const user = broadcastData.userId as BroadcastUser;
        const seller: SellerInfo = {
          _id: user._id,
          firstName: user.firstName ?? "",
          lastName: user.lastName ?? "",
          companyName: user.companyName,
          email: user.email ?? user.companyEmail,
          telCompany: user.telCompany,
          website: user.website,
          country: user.country,
        };

        const title =
          bcItem.title ||
          `${bcItem.manufacturer} ${bcItem.partNumber || bcItem.model || ""}`.trim();
        const category = bcItem.categories?.[0] ?? "—";

        const itemDetail: ItemDetail = {
          _id: `${broadcastId}-${parsedItemNo}`,
          title: title || "—",
          manufacturer: bcItem.manufacturer,
          model: bcItem.model ?? undefined,
          partNumber: bcItem.partNumber ?? undefined,
          category,
          condition: bcItem.condition ?? "—",
          quantity: bcItem.quantity,
          price: bcItem.price ?? undefined,
          currency: bcItem.currency ?? "USD",
          country: bcItem.country ?? undefined,
          description: bcItem.description ?? undefined,
          sellerId: seller,
        };

        setItem(itemDetail);
      } catch (err: unknown) {
        const errMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          "We couldn't load this broadcast. Please refresh the page";
        setError(errMessage);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(err as Error, "We couldn't load this broadcast. Please refresh the page");
      } finally {
        setLoading(false);
      }
    };

    fetchBroadcast();
  }, [broadcastId, itemNo]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container className="item-detail-page">
        <div className="item-detail-page__header">
          <Button variant="outline-secondary" onClick={handleBack}>
            ← Back
          </Button>
        </div>
        <div className="item-detail-page__loading">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error || !item) {
    return (
      <Container className="item-detail-page">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error || "Item not found"}</p>
          <Button variant="outline-danger" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  const seller: SellerInfo =
    typeof item.sellerId === "object" && item.sellerId !== null
      ? (item.sellerId as SellerInfo)
      : {
          _id: typeof item.sellerId === "string" ? item.sellerId : "",
          firstName: "",
          lastName: "",
        };

  const companyName =
    seller.companyName || `${seller.firstName} ${seller.lastName}`.trim() || "Company";
  const sellerId = seller._id || (typeof item.sellerId === "string" ? item.sellerId : "");

  return (
    <Container className="item-detail-page">
      {/* [2026-02-10] Back button */}
      <div className="item-detail-page__back">
        <button className="btn-back" onClick={handleBack}>
          ← Back
        </button>
      </div>

      {/* [2026-02-10] Hero Banner with title + meta badges */}
      <div className="item-detail-page__hero">
        <h1 className="item-detail-page__hero-title">{item.title}</h1>
        <div className="item-detail-page__hero-meta">
          <span className={`badge-type badge-type--${broadcastType.toLowerCase()}`}>
            {broadcastType}
          </span>
          {item.manufacturer && (
            <span className="item-detail-page__hero-tag">
              <strong>{item.manufacturer}</strong>
            </span>
          )}
          {item.partNumber && (
            <span className="item-detail-page__hero-tag">
              SKU: <strong>{item.partNumber}</strong>
            </span>
          )}
          {item.condition && item.condition !== "—" && (
            <span className={`badge-condition ${getConditionClass(item.condition)}`}>
              {item.condition}
            </span>
          )}
          <span className="item-detail-page__hero-tag">
            QTY: <strong>{item.quantity}</strong>
          </span>
          {item.price && (
            <span className="item-detail-page__hero-tag item-detail-page__hero-tag--price">
              {formatPrice(item.price, item.currency)}
            </span>
          )}
        </div>
      </div>

      {/* [2026-02-10] Body Grid: Specs table (left) + Company sidebar (right) */}
      <div className="item-detail-page__body">
        <div className="item-detail-page__main">
          <ItemInformation item={item} />
          {sellerId && (
            <RecentBroadcasts sellerId={sellerId} companyName={companyName} />
          )}
        </div>
        {sellerId && (
          <div className="item-detail-page__sidebar">
            <CompanyInformation seller={seller} />
          </div>
        )}
      </div>
    </Container>
  );
};

export default BroadcastItemDetailPage;
